import React from 'react';
import { addCommas } from '@persian-tools/persian-tools';
import { useCallback, useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import { EngToPerConverter, timeConverter, toGregorian } from 'utils/utils';
import { ChartDataItem, ChartPeriod } from './gateway-chart';
import { SelectChangeEvent } from '@mui/material';
import { useGatewayChartQuery, useTransactionChartQuery } from 'app/store';
import { useGateway } from 'hooks';

type ChartType = 'income' | 'transaction';

const queryHookMap = {
    income: useGatewayChartQuery,
    transaction: useTransactionChartQuery,
};

const seriesNameMap: Record<ChartType, string> = {
    income: 'مبلغ درآمد',
    transaction: 'تعداد تراکنش',
};

const valueSuffixMap: Record<ChartType, string> = {
    income: 'ریال',
    transaction: 'عدد',
};

const defaultOptions = (
    valueSuffix: string,
    brandColor: string,
    labelColor: string,
    tooltipClassName: string,
) => ({
    colors: [brandColor],
    chart: {
        width: '100%',
        fontFamily: 'IRANSans',
        type: 'area',
        toolbar: { show: false },
        zoom: { enabled: false },
    },
    dataLabels: { enabled: false },
    stroke: {
        curve: 'smooth',
        width: 3,
    },
    xaxis: {
        tooltip: { enabled: false },
        axisTicks: { show: false },
        labels: { show: false },
        show: false,
        tickPlacement: 'on',
        tickAmount: 0,
    },
    yaxis: {
        tickPlacement: 'between',
        labels: {
            style: { colors: labelColor },
            show: true,
            align: 'right',
            offsetX: 0,
            offsetY: 0,
            formatter: (val: any) =>
                EngToPerConverter(addCommas(val) + ` ${valueSuffix}`),
        },
    },
    tooltip: {
        marker: { show: false },
        cssClass: tooltipClassName,
        y: {
            formatter: (val: any) =>
                EngToPerConverter(addCommas(val) + ` ${valueSuffix}`),
        },
    },
});

const defaultData: ChartDataItem[] = [
    { name: 'daily', label: 'روزانه' },
    { name: 'weekly', label: 'هفتگی' },
    { name: 'monthly', label: 'ماهیانه' },
    { name: 'threeMonths', label: 'سه ماهه' },
    { name: 'sixMonths', label: 'شش ماهه' },
    { name: 'yearly', label: 'سالیانه' },
    { name: 'custom', label: 'انتخاب بازه دلخواه' },
];

const useGatewayChart = ({
    chartType,
    staticChart,
    fixedTimeline,
    disabled = false,
}: {
    chartType: ChartType;
    staticChart?: Record<string, number | string> | undefined;
    fixedTimeline?: ChartPeriod;
    disabled?: boolean;
}) => {
    const theme = useTheme();
    const active = useGateway();

    const queryHook = queryHookMap[chartType];
    const seriesName = seriesNameMap[chartType];
    const valueSuffix = valueSuffixMap[chartType];

    const [timeline, setTimeline] = useState<ChartPeriod>(
        fixedTimeline ?? 'daily',
    );
    const [series, updateSeries] = useState<any>([]);
    const [options, setOptions] = useState<any>(
        defaultOptions(
            valueSuffix,
            theme.palette.primary.main,
            theme.palette.text.secondary,
            'apex-tooltip-theme',
        ),
    );
    const [period, setPeriod] = useState<ChartPeriod | string[]>(timeline);
    const [selectedDateRange, setSelectedDateRange] = useState<string[]>([]);
    const [datepickerOpen, setDatepickerOpen] = useState<boolean>(false);
    const [data, setData] = useState<ChartDataItem[]>(defaultData);

    const { data: chartData, isLoading } = queryHook(
        { gateway: active?.id!, period },
        { skip: disabled || !period || !active?.id || Boolean(staticChart) },
    );

    useEffect(() => {
        setData(defaultData);
        setTimeline(fixedTimeline ?? 'daily');
        setOptions(
            defaultOptions(
                valueSuffix,
                theme.palette.primary.main,
                theme.palette.text.secondary,
                'apex-tooltip-theme',
            ),
        );
    }, [chartType, theme]);

    useEffect(() => {
        if (!staticChart && !chartData) return;

        const currentData = staticChart ?? chartData;

        const index = data?.findIndex((item) => item.name === timeline);
        if (index === -1) return;

        const format =
            timeline === 'daily'
                ? (day: string) => timeConverter(day)
                : timeline === 'weekly'
                  ? (day: string) => timeConverter(day, 'fullDay')
                  : (day: string) => timeConverter(day, 'date');

        const updatedData = [...data];
        updatedData[index] = {
            ...updatedData[index],
            label: Array.isArray(period)
                ? `از ${timeConverter(period[0], 'date')} تا ${timeConverter(period[1], 'date')}`
                : updatedData[index]?.label,
            categories: Object.keys(currentData).map((day) => format(day)),
            series: [
                {
                    name: seriesName,
                    data: Object.values(currentData),
                },
            ],
        };

        setData(updatedData);
    }, [chartData, staticChart]);

    useEffect(() => {
        const index = data.findIndex((item) => item.name === timeline);

        if (index === -1) return;

        if (data[index]?.series && data[index]?.categories) {
            updateSeries(data[index].series);
            handleChangeOptions(data[index]?.categories ?? []);
        }
    }, [data]);

    useEffect(() => {
        if (timeline !== 'custom') {
            setPeriod(timeline);
        } else if (selectedDateRange.length > 0) {
            setPeriod(selectedDateRange);
        }
    }, [timeline, selectedDateRange]);

    const handleChangeOptions = useCallback(
        (categories: string[]) => {
            setOptions((prev: any) => ({
                ...prev,
                xaxis: {
                    ...prev.xaxis,
                    categories,
                },
            }));
        },
        [timeline],
    );

    const handleChange = (event: SelectChangeEvent) => {
        const newValue = event.target.value as ChartPeriod;

        setTimeline(newValue);
        if (newValue === 'custom') setDatepickerOpen(true);
    };

    const handleDatePickerChange = (range: string[]) => {
        const finalDate = [toGregorian(range[0]), toGregorian(range[1])];
        setSelectedDateRange(finalDate);
    };

    return {
        data,
        timeline,
        series,
        options,
        isLoading,
        datepickerOpen,
        setDatepickerOpen,
        onDatePickerChange: handleDatePickerChange,
        onChange: handleChange,
    };
};

export default useGatewayChart;
